import { Meeting, File } from 'api';
import type { AppEpic } from 'model';
import { combineEpics } from 'redux-observable';
import { filter, map, mergeMap, startWith, catchError, delay, withLatestFrom, debounceTime } from 'rxjs/operators';
import { EMPTY, of, combineLatest, merge, forkJoin, from } from 'rxjs';
import { push, replace } from 'connected-react-router';
import { appMessageClose, appMessageError, appMessageLoading, appMessageSuccess } from 'model/app';
import APP_MESSAGE from 'constant/message';
import tracker from 'lib/tracker';
import * as TMeeting from 'type/meeting';

import {
  orderMeeting,
  updateMeeting,
  setMeetingInfo,
  getMeetingDetails,
  deleteMeeting,
  quitMeeting,
  changeAttendState,
  addMeetingMember,
  deleteMeetingMember,
  uploadFile,
  getFilesInfo,
  setMeetingFileByList,
  updateMeetingFile,
  addMeetingFile,
  deleteMeetingFile,
  cancelMeeting,
  revokeMeeting,
  closeMeeting,
  chgAttendState,
  getUsersMeetings,
  getUsersMeetingPage,
  getCalendarMeetings,
  setMeetingRecord,
  setMeetingRecordsPageInfo,
  setMeetingRecordsLoading,
  setCalendarMeetingRecords,
  Handles,
  getMeetingSummary,
  setMeetingSummary,
  publishMeetingSummary,
  publishMeetingContent,
  getMeetingIotDevices,
  setIotMessage,
  continueMeeting,
  setHasContinueOperation,
  getContinueMeetingOperation,
  getMeetingThemeConfig,
  setMeetingThemeConfig,
  getMeetingCalendarDetailList,
  setMeetingCalendarDetailList,
  setMeetingCalendarLoading,
  getMeetingDetailsV2,
  setMeetingDetailV2,
  cancelMeetingV2,
  cancelThisRepeatMeeting,
  cancelRepeatMeeting,
  closeMeetingV2,
  getOrderSettings,
  setOrderSettings,
  getRecentlyList,
  setRecentlyList
} from '.';
import { MeetingState, OrderMeetingV2 } from 'type/meeting';
import APP_ROUTE from 'constant/route';
import { setReadMeetingRecord } from 'model/user';
import qs from 'qs';
import { RedirectPage } from 'util/meeting';
import { MeetingOptionType } from 'type/meetingDetail';

type OrderMeetingAction = ReturnType<typeof orderMeeting>;
type UpdateMeetingAction = ReturnType<typeof updateMeeting>;
type GetMeetingAction = ReturnType<typeof getMeetingDetails>;
type GetMeetingDetailsV2Action = ReturnType<typeof getMeetingDetailsV2>;
type DeleteMeetingAction = ReturnType<typeof deleteMeeting>;
type QuitMeetingAction = ReturnType<typeof quitMeeting>;
type changeAttendStateAction = ReturnType<typeof changeAttendState>;
type AddMeetingMemberAction = ReturnType<typeof addMeetingMember>;
type DeleteMeetingMemberAction = ReturnType<typeof deleteMeetingMember>;
type UploadFileAction = ReturnType<typeof uploadFile>;
type GetFilesInfoAction = ReturnType<typeof getFilesInfo>;
type AddMeetingFileAction = ReturnType<typeof addMeetingFile>;
type DeleteMeetingFileAction = ReturnType<typeof deleteMeetingFile>;
type CancelMeetingAction = ReturnType<typeof cancelMeeting>;
type cancelMeetingV2Action = ReturnType<typeof cancelMeetingV2>;
type cancelThisRepeatMeetingAction = ReturnType<typeof cancelThisRepeatMeeting>;
type cancelAllRepeatMeetingAction = ReturnType<typeof cancelRepeatMeeting>;
type RevokeMeetingAction = ReturnType<typeof revokeMeeting>;
type CloseMeetingAction = ReturnType<typeof closeMeeting>;
type CloseMeetingV2Action = ReturnType<typeof closeMeetingV2>;
type GetUsersMeetingsAction = ReturnType<typeof getUsersMeetings>;
type GetUsersMeetingPageAction = ReturnType<typeof getUsersMeetingPage>;
type GetMeetingSummaryAction = ReturnType<typeof getMeetingSummary>;
type PublishMeetingSummaryAction = ReturnType<typeof publishMeetingSummary>;
type PublishMeetingContentAction = ReturnType<typeof publishMeetingContent>;
type getMeetingIotDevicesAction = ReturnType<typeof getMeetingIotDevices>;
type ContinueMeetingAction = ReturnType<typeof continueMeeting>;
type getContinueMeetingOperationAction = ReturnType<typeof getContinueMeetingOperation>;

// 会议操作类型
type MeetingHandles = {
  type: Handles;
  data?: any;
};

const closeOrRedirect = (action: MeetingHandles) => () => {
  if (action?.data?.path) return action.data?.path;
  return APP_ROUTE.HOME;
};

const getRedirectQuery = () => {
  const query = qs.parse(location.search.slice(1));
  const data = { path: '' };
  if (query.from === RedirectPage.SCHEDULE) {
    data.path = APP_ROUTE.SCHEDULE;
  }
  return data;
};

const orderMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(orderMeeting.match),
    debounceTime(300),
    mergeMap((action: OrderMeetingAction) => {
      /** 区分周期会议和普通会议 */
      const request = action.payload.meeting.isRepeatMeeting ? Meeting.orderRepeatMeetingV2 : Meeting.orderMeetingV2;
      console.log('区分周期会议和普通会议');
      return request(action.payload.meeting).pipe(
        mergeMap(({ data }) => {
          // 执行成功回调
          action.payload.onSuccess?.(data);
          tracker?.reporteMeetingConfig(data);
          // 无需跳转
          if (action.payload.redirect === false) {
            return of(appMessageSuccess(APP_MESSAGE.ORDER_MEETING_SUCCESS));
          } else {
            // 重定向
            return of(appMessageSuccess(APP_MESSAGE.ORDER_MEETING_SUCCESS), replace(`/details/${data.meetingNo}` + `?from=${RedirectPage.SCHEDULE}`));
          }
        }),
        catchError((err) => {
          console.log('error', err.response);
          const code = err.response.data.code;
          // 执行错误回调
          action.payload.onFail?.(err);

          if (code === 400405001) {
            return of(appMessageError(APP_MESSAGE.ORDER_MEETING_TIMELOADER));
          } else if (code === 400405022) {
            return of(appMessageError(APP_MESSAGE.APPROVAL_MEETING_MUSET_AFTER_10));
          } else if (code === 400405002) {
            return of(appMessageError(APP_MESSAGE.ORDER_MEETING_ERROR));
          } else if (code === 400405018) {
            return of(appMessageError(APP_MESSAGE.APPROVAL_MEETING_HAVE_NO_AUDIT));
          } else if (code === 400403002) {
            // 日程冲突
            return of(appMessageError(APP_MESSAGE.TIME_PERIOD_IS_OCCUPIED));
          } else if (code === 400403001) {
            return of(appMessageError(APP_MESSAGE.TIME_PERIOD_IS_DISABLED));
          } else if (code === 400416023) {
            return of(appMessageError(APP_MESSAGE.ROOM_IS_DISABLED));
          } else if (code === 400403007) {
            return of(appMessageError(APP_MESSAGE.TIME_PERIOD_IS_EXPIRED));
          } else if (code === 400405020) {
            return of(appMessageError(APP_MESSAGE.OPERATION_NOT_ALLOW));
          } else if (code === 400416015) {
            return of(appMessageError(APP_MESSAGE.ROOM_NOT_ALLOW));
          } else if (code === 400416028) {
            return of(appMessageError(APP_MESSAGE.ORDER_MEETING_PERIOD_REJECTED));
          } else {
            return of(appMessageError(APP_MESSAGE.ORDER_MEETING_REJECTED));
          }
        }),
        startWith(appMessageLoading(APP_MESSAGE.ORDER_MEETING_PENDING))
      );
    })
  );

const updateMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(updateMeeting.match),
    debounceTime(300),
    mergeMap((action: UpdateMeetingAction) => {
      const { meetingId, repeatMeetingNo, type, params, onSuccess, onFail } = action.payload;
      /** 区分：修改单次周期会议、修改全部周期会议、普通会议 */
      let request;
      if (type === MeetingOptionType.AllMeeting && repeatMeetingNo) {
        request = (params: OrderMeetingV2) => Meeting.updateAllRepeatMeetingV2(repeatMeetingNo, params);
      } else if (type === MeetingOptionType.ThisMeeting) {
        request = (params: OrderMeetingV2) => Meeting.updateSingleRepeatMeetingV2(meetingId, params);
      } else {
        request = (params: OrderMeetingV2) => Meeting.updateMeetingV2(meetingId, params);
      }

      return request(params).pipe(
        mergeMap(() => {
          // 执行成功回调
          onSuccess?.();
          if (action.payload.redirect === false) {
            return of(appMessageClose());
          } else {
            return of(appMessageClose(), replace(`/details/${meetingId}${location.search}`));
          }
        }),
        catchError((err) => {
          console.log('update err', err);
          // 执行错误回调
          onFail?.(err);

          const code = err.response.data.code;
          if (code === 400405001) {
            return of(appMessageError(APP_MESSAGE.ORDER_MEETING_TIMELOADER));
          } else if (code === 400403002) {
            // 日程冲突
            return of(appMessageError(APP_MESSAGE.TIME_PERIOD_IS_OCCUPIED));
          } else if (code === 400405017 || code === 400405025) {
            return of(appMessageError(APP_MESSAGE.MEETING_ROOM_NEEDS_APPROVAL));
          } else {
            return of(appMessageError(APP_MESSAGE.UPDATE_MEETING_REJECTED));
          }
        }),
        startWith(appMessageLoading(APP_MESSAGE.UPDATE_MEETING_PENDING))
      );
    })
  );

const getMeetingInfoEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getMeetingDetails.match),
    mergeMap((action: GetMeetingAction) => {
      // 兼容审批会议,简单粗暴的判断方式，如果是数字，就是正常会议，如果是uuid则是审批会议
      const isApprovalMeeting = isNaN(Number(action.payload));
      console.log('isApprovalMeeting', action.payload, isApprovalMeeting);
      const searchMeetings$ = Meeting.searchMeeting(action.payload).pipe(catchError(() => of(false)));
      const searchApprovalMeetings$ = isApprovalMeeting ? Meeting.searchApprovalMeeting(action.payload).pipe(catchError(() => of(false))) : of(null);
      // 兼容审批会议
      return merge(searchMeetings$, searchApprovalMeetings$).pipe(
        filter((data) => Boolean(data)),
        mergeMap((res) => {
          const data = (res as any)?.data || res;
          return of(
            getFilesInfo({ shareId: data.shareId, accessKey: data.accessKey }),
            setMeetingInfo({
              ...data
            })
          );
        })
      );
    })
  );

const getMeetingDetailsV2Epic: AppEpic = (action$) =>
  action$.pipe(
    filter(getMeetingDetailsV2.match),
    mergeMap((action: GetMeetingDetailsV2Action) =>
      Meeting.getMeetingDetailsV2(action.payload).pipe(
        mergeMap((response) => {
          const data = response.data;
          console.log('V2获取会议详情：', data);
          return of(setMeetingDetailV2(data));
        })
      )
    )
  );

const deleteMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(deleteMeeting.match),
    mergeMap((action: DeleteMeetingAction) => {
      return Meeting.deleteMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.DETELE, data: getRedirectQuery() })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.DELETE_MEETING_SUCCESS))),
        startWith(appMessageLoading(APP_MESSAGE.DELETE_MEETING_PENDING)),
        catchError(() => of(appMessageError(APP_MESSAGE.DELETE_MEETING_REJECTED)))
      );
    })
  );

const quitMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(quitMeeting.match),
    mergeMap((action: QuitMeetingAction) =>
      Meeting.quitMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.QUIT, data: getRedirectQuery() })),
        mergeMap((path) => of(appMessageSuccess(APP_MESSAGE.DEFAULT_SUCCESS), push(path)).pipe(delay(200))),
        startWith(appMessageLoading(APP_MESSAGE.DEFAULT_PENDING)),
        catchError(() => of(appMessageError(APP_MESSAGE.DEFAULT_REJECTED)))
      )
    )
  );

const changeAttendStateEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(changeAttendState.match),
    mergeMap((action: changeAttendStateAction) =>
      Meeting.changeMemberStatus(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CHANGEATTENDSTATE, data: { state: action.payload.state } })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.DEFAULT_SUCCESS), chgAttendState(action.payload.state))),
        startWith(appMessageLoading(APP_MESSAGE.DEFAULT_PENDING)),
        catchError(() => of(appMessageError(APP_MESSAGE.DEFAULT_REJECTED)))
      )
    )
  );

const addMeetingMemberEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(addMeetingMember.match),
    mergeMap((action: AddMeetingMemberAction) =>
      Meeting.addMeetingMember(action.payload.meetingId, action.payload.members).pipe(
        mergeMap(() => of(appMessageClose(), getMeetingDetailsV2(action.payload.meetingId))),
        catchError(() => of(appMessageError(APP_MESSAGE.ADD_MEETING_MEMBER_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.ADD_MEETING_MEMBER_PENDING))
      )
    )
  );

const deleteMeetingMemberEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(deleteMeetingMember.match),
    mergeMap((action: DeleteMeetingMemberAction) =>
      Meeting.deleteMeetingMember(action.payload.meetingId, action.payload.members).pipe(
        mergeMap(() => of(appMessageClose(), getMeetingDetailsV2(action.payload.meetingId), appMessageSuccess(APP_MESSAGE.DELETE_MEETING_MEMBER_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.DELETE_MEETING_MEMBER_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.DELETE_MEETING_MEMBER_PENDING))
      )
    )
  );

// 上传文件的流
const getUploadFile = (file: TMeeting.MeetingFile) => {
  return File.uploadFile(file.formData as FormData).pipe(
    mergeMap(([percent, data]) => {
      return of({
        data,
        action: updateMeetingFile({
          ...file,
          itemId: data ? data.id : file.itemId,
          contentType: (data && data.contentType) || '',
          url: (data && data.downloadUrl) || '',
          status: percent !== 100 && !data ? 'loading' : 'success',
          percent: percent
        })
      });
    }),
    catchError((err) => {
      return of({
        data: null,
        err,
        action: updateMeetingFile({
          ...file,
          status: 'fail',
          percent: 0
        })
      });
    })
  );
};

// 获取已关联的文件
const getFilesInfoEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getFilesInfo.match),
    mergeMap((action: GetFilesInfoAction) => {
      if (!action.payload.shareId) return of(setMeetingFileByList([]));
      return from(Meeting.getFilesInfo(action.payload.shareId, action.payload.accessKey)).pipe(
        mergeMap((fileList) => {
          /**
         * 可能会存在这样的数据，过滤掉
          author: "Teams"
          childrenNums: 0
          deviceName: "Teams"
          indexOf: 0
          isTransient: false
          size: 0
         */
          return of(setMeetingFileByList(fileList.filter((file) => file.itemId)));
        }),
        catchError(() => of(appMessageError(APP_MESSAGE.GET_MEETING_FILE_REJECTED)))
      );
    })
  );

// 上传文件
const uploadFileEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(uploadFile.match),
    mergeMap((action: UploadFileAction) => {
      return combineLatest(action.payload.map((file) => getUploadFile(file))).pipe(
        mergeMap((results) => {
          return of(...results.filter((d) => d && d.action).map((d) => d.action));
        })
      );
    })
  );

// 上传并关联会议文件
const addMeetingFileEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(addMeetingFile.match),
    mergeMap((action: AddMeetingFileAction) => {
      const meetingId = action.payload.meetingId;
      const shareId = action.payload.shareId;
      const accessKey = action.payload.accessKey;
      return combineLatest(action.payload.fileList.map((file) => getUploadFile(file))).pipe(
        mergeMap((results) => {
          const isComplete = !results.find((d) => !d || !d.data);
          if (!isComplete) {
            return results.filter((d) => d).map((d) => d.action);
          }

          const filesInfo = results.filter((d) => d.data).map((d) => d.data) as TMeeting.MeetFileUploadRes[];
          return Meeting.addMeetingFile(meetingId, filesInfo).pipe(
            mergeMap(() => of(getFilesInfo({ shareId, accessKey }))),
            catchError(() => of(appMessageError(APP_MESSAGE.ADD_MEETING_FILE_REJECTED)))
          );
        })
      );
    })
  );

const deleteMeetingFileEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(deleteMeetingFile.match),
    mergeMap((action: DeleteMeetingFileAction) =>
      Meeting.deleteMeetingFile(action.payload.meetingId, action.payload.fileIds).pipe(
        mergeMap(() => of(appMessageClose(), getFilesInfo({ shareId: action.payload.shareId, accessKey: action.payload.accessKey }))),
        catchError(() => of(appMessageError(APP_MESSAGE.DELETE_MEETING_FILE_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.DELETE_MEETING_FILE_PENDING))
      )
    )
  );

const cancelMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(cancelMeeting.match),
    mergeMap((action: CancelMeetingAction) =>
      Meeting.cancelMeeting(action.payload.meetingNo).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CANCEL })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.CANCEL_MEETING_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.CANCEL_MEETING_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CANCEL_MEETING_PENDING))
      )
    )
  );

// 取消单个会议
// 取消单个会议
const cancelMeetingV2Epic: AppEpic = (action$) =>
  action$.pipe(
    filter(cancelMeetingV2.match),
    mergeMap((action: cancelMeetingV2Action) =>
      Meeting.cancelMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CANCEL })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.CANCEL_MEETING_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.CANCEL_MEETING_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CANCEL_MEETING_PENDING))
      )
    )
  );

// 取消重复会议中本次会议【待联调】
const cancelThisRepeatMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(cancelThisRepeatMeeting.match),
    mergeMap((action: cancelThisRepeatMeetingAction) =>
      Meeting.cancelThisMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CANCEL })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.CANCEL_MEETING_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.CANCEL_MEETING_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CANCEL_MEETING_PENDING))
      )
    )
  );

// 取消所有重复会议或取消当前及之后的重复会议【待联调】
const cancelRepeatMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(cancelRepeatMeeting.match),
    mergeMap((action: cancelAllRepeatMeetingAction) =>
      Meeting.cancelAllMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CANCEL })),
        mergeMap(() => of(appMessageSuccess(APP_MESSAGE.CANCEL_MEETING_SUCCESS))),
        catchError(() => of(appMessageError(APP_MESSAGE.CANCEL_MEETING_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.CANCEL_MEETING_PENDING))
      )
    )
  );

const revokeMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(revokeMeeting.match),
    mergeMap((action: RevokeMeetingAction) =>
      Meeting.revokeMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CANCEL })),
        mergeMap(() => of(getMeetingDetailsV2(action.payload), appMessageSuccess(APP_MESSAGE.REVOKE_MEETING_SUCCESS)).pipe(delay(200))),
        catchError(() => of(appMessageError(APP_MESSAGE.REVOKE_MEETING_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.REVOKE_MEETING_PENDING))
      )
    )
  );

const closeMeetingEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(closeMeeting.match),
    mergeMap((action: CloseMeetingAction) =>
      Meeting.closeMeeting(action.payload.meetingNo || action.payload._id).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CLOSE })),
        mergeMap(() =>
          of(
            appMessageSuccess(APP_MESSAGE.CLOSE_MEETING_SUCCESS),
            setMeetingInfo({
              ...action.payload,
              state: MeetingState.END
            })
          )
        ),
        catchError((err) => {
          if (err.response.data.code === 400403013) {
            return of(appMessageError('会议已结束'), push('/details/' + action.payload.meetingNo));
          }
          return of(appMessageError(APP_MESSAGE.CLOSE_MEETING_REJECTED));
        }),
        startWith(appMessageLoading(APP_MESSAGE.CLOSE_MEETING_PENDING))
      )
    )
  );

const closeMeetingV2Epic: AppEpic = (action$) =>
  action$.pipe(
    filter(closeMeetingV2.match),
    mergeMap((action: CloseMeetingV2Action) =>
      Meeting.closeMeeting(action.payload).pipe(
        withLatestFrom(closeOrRedirect({ type: Handles.CLOSE })),
        mergeMap(() => of(getMeetingDetailsV2(action.payload), appMessageSuccess(APP_MESSAGE.CLOSE_MEETING_SUCCESS))),
        catchError((err) => {
          if (err.response.data.code === 400403013) {
            return of(appMessageError('会议已结束'), push('/details/' + action.payload));
          }
          return of(appMessageError(APP_MESSAGE.CLOSE_MEETING_REJECTED));
        }),
        startWith(appMessageLoading(APP_MESSAGE.CLOSE_MEETING_PENDING))
      )
    )
  );

const MEETING_RECORD_PAGE_SIZE = 20;

const getUsersMeetingsEpic: AppEpic = (action$) => {
  return action$.pipe(
    // debounceTime(500),
    filter(getUsersMeetings.match),
    debounceTime(300),
    mergeMap((action: GetUsersMeetingsAction) => {
      const { silent, ...params } = action.payload;
      const request$ = Meeting.getMeetingByUserId(params).pipe(
        mergeMap((data) => (silent ? of(setMeetingRecord(data.data)) : of(setMeetingRecordsLoading(false), setMeetingRecord(data.data)))),
        catchError(() => (silent ? EMPTY : of(setMeetingRecordsLoading(false))))
      );

      return silent ? request$ : request$.pipe(startWith(setMeetingRecordsLoading(true)));
    })
  );
};

const getUsersMeetingPageEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filter(getUsersMeetingPage.match),
    debounceTime(300),
    mergeMap((action: GetUsersMeetingPageAction) => {
      const { silent, ...params } = action.payload;
      const pageNo = params.pageNo || 1;
      const pageSize = params.pageSize || MEETING_RECORD_PAGE_SIZE;

      const request$ = Meeting.getMeetingRecordPage({ ...params, pageNo, pageSize }).pipe(
        withLatestFrom(state$),
        mergeMap(([response, state]) => {
          const page = response?.data;
          const list = page?.data || [];
          const nextRecords = pageNo > 1 ? state.meeting.meetingRecords.concat(list) : list;

          const actions = [
            setMeetingRecord(nextRecords),
            setMeetingRecordsPageInfo({
              pageNo: page?.pageNo ?? pageNo,
              pageSize: page?.pageSize ?? pageSize,
              total: page?.total ?? 0
            })
          ];

          return silent ? of(...actions) : of(setMeetingRecordsLoading(false), ...actions);
        }),
        catchError(() => (silent ? EMPTY : of(setMeetingRecordsLoading(false))))
      );

      return silent ? request$ : request$.pipe(startWith(setMeetingRecordsLoading(true)));
    })
  );
};

const getCalendarMeetingRecordsEpic: AppEpic = (action$) => {
  return action$.pipe(
    // debounceTime(500),
    filter(getCalendarMeetings.match),
    debounceTime(300),
    mergeMap((action: GetUsersMeetingsAction) => Meeting.getMeetingByUserId(action.payload).pipe(map((data) => setCalendarMeetingRecords(data.data))))
  );
};

const getMeetingCalendarDetailListEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getMeetingCalendarDetailList.match),
    debounceTime(300),
    mergeMap((action: GetUsersMeetingsAction) => {
      const { silent, ...params } = action.payload;
      const request$ = Meeting.getMeetingRecordOfCalendarList(params).pipe(
        mergeMap((data) =>
          silent
            ? of(setMeetingCalendarDetailList(data?.data || []))
            : of(setMeetingCalendarLoading(false), setMeetingCalendarDetailList(data?.data || []))
        ),
        catchError(() =>
          silent
            ? EMPTY
            : of(
              setMeetingCalendarLoading(false),
              setMeetingCalendarDetailList(new Array(42))
              // todo mock
              // setMeetingCalendarDetailList(CalendarMockList)
            )
        )
      );

      return silent ? request$ : request$.pipe(startWith(setMeetingCalendarLoading(true)));
    })
  );
};

const getMeetingSummaryEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getMeetingSummary.match),
    debounceTime(500),
    mergeMap((action: GetMeetingSummaryAction) =>
      Meeting.getSummary(action.payload).pipe(
        map((data) => setMeetingSummary(data)),
        catchError(() => {
          return of(setMeetingSummary(null)); // 假设你有一个 setMeetingSummaryError 动作来处理错误
        })
      )
    )
  );
};

const publishMeetingSummaryEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(publishMeetingSummary.match),
    debounceTime(500),
    mergeMap((action: PublishMeetingSummaryAction) =>
      Meeting.publicSummary(action.payload.meetingId, action.payload.subject, action.payload.content).pipe(
        mergeMap(() =>
          of(
            getMeetingDetailsV2(action.payload.meetingId),
            getMeetingSummary(action.payload.meetingId),
            appMessageSuccess(APP_MESSAGE.PUBLISH_SUMMARY_SUCCESS),
            setReadMeetingRecord({ staffId: action.payload.staffId, meetingId: action.payload.meetingId })
          )
        ),
        catchError(() => of(appMessageError(APP_MESSAGE.PUBLISH_SUMMARY_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.PUBLISH_SUMMARY_PENGING))
      )
    )
  );
};

const publishMeetingContentEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(publishMeetingContent.match),
    debounceTime(500),
    mergeMap((action: PublishMeetingContentAction) =>
      Meeting.publicContent(action.payload.meetingId, action.payload.content).pipe(
        mergeMap(() => of(getMeetingDetailsV2(action.payload.meetingId), appMessageSuccess(APP_MESSAGE.SAVE_PENGING))),
        catchError(() => of(appMessageError(APP_MESSAGE.SAVE_REJECTED))),
        startWith(appMessageLoading(APP_MESSAGE.PUBLISH_SUMMARY_PENGING))
      )
    )
  );
};

const getMeetingIotDevicesEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getMeetingIotDevices.match),
    debounceTime(500),
    mergeMap((action: getMeetingIotDevicesAction) => Meeting.getIotDevices(action.payload).pipe(map((data) => setIotMessage(data))))
  );
};

const continueMeetingEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(continueMeeting.match),
    debounceTime(500),
    mergeMap((action: ContinueMeetingAction) =>
      Meeting.continueMeeting(action.payload).pipe(
        mergeMap(() => of(setHasContinueOperation(true))),
        catchError((err) => {
          const code = err?.response?.data?.code;
          if (code === 400405023) {
            return of(appMessageError('已在其他地方操作'), setHasContinueOperation(true));
          } else if (code === 400403013) {
            return of(appMessageError('会议已结束'), push('/details/' + action.payload));
          }
          return of(appMessageError('操作失败'));
        })
      )
    )
  );
};

const getContinueMeetingOperationEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getContinueMeetingOperation.match),
    debounceTime(500),
    mergeMap((action: getContinueMeetingOperationAction) =>
      Meeting.getContinueMeetingOperation(action.payload).pipe(map(({ data }) => setHasContinueOperation(!!data?.length)))
    )
  );
};

const getMeetingThemeConfigEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getMeetingThemeConfig.match),
    mergeMap((action: any) =>
      Meeting.getCompanyconfigs(action.payload).pipe(
        map((data) => {
          return setMeetingThemeConfig(data.data);
        })
      )
    )
  );
};

const getOrderSettingsEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getOrderSettings.match),
    mergeMap(() => {
      return forkJoin([Meeting.getOrderSettings(), Meeting.getMeetingConfig()]).pipe(
        mergeMap(([orderSettingsRes, orderConfigRes]) => {
          try {
            const orderSettings = JSON.parse(orderSettingsRes.data.template).createMode;
            console.log('获取会议配置成功 ===>', orderSettings);
            return of(setOrderSettings({ ...orderSettings, ...orderConfigRes.data }));
          } catch (err) {
            console.log('获取会议配置失败', err);
            return of(setOrderSettings({ ...orderConfigRes.data }));
          }
        })
      );
    })
  );
};

const getRecentlyListEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(getRecentlyList.match),
    mergeMap(() =>
      Meeting.getRecentlyList().pipe(
        mergeMap((result) =>
          of(
            setRecentlyList(
              (result.data || []).map((item: any) => {
                return {
                  ...item,
                  id: `MEETING-${item.meetingNo}`,
                  type: 'MEETING',
                  teamName: `${item?.theme}（${item?.meetingMemberList?.length || 0}）`,
                  memberDetailResponseList: (item.meetingMemberList || []).map((menber: any) => {
                    return {
                      ...menber
                    };
                  })
                };
              })
            )
          )
        )
      )
    )
  );
};

export type MeetingEpicActions =
  | OrderMeetingAction
  | UpdateMeetingAction
  | GetMeetingAction
  | DeleteMeetingAction
  | QuitMeetingAction
  | changeAttendStateAction
  | AddMeetingMemberAction
  | DeleteMeetingMemberAction
  | GetMeetingSummaryAction
  | PublishMeetingSummaryAction;

export default combineEpics(
  orderMeetingEpic,
  updateMeetingEpic,
  getMeetingInfoEpic,
  getMeetingDetailsV2Epic,
  deleteMeetingEpic,
  quitMeetingEpic,
  changeAttendStateEpic,
  addMeetingMemberEpic,
  deleteMeetingMemberEpic,
  uploadFileEpic,
  getFilesInfoEpic,
  addMeetingFileEpic,
  deleteMeetingFileEpic,
  cancelMeetingEpic,
  cancelMeetingV2Epic,
  cancelThisRepeatMeetingEpic,
  cancelRepeatMeetingEpic,
  closeMeetingEpic,
  closeMeetingV2Epic,
  getUsersMeetingsEpic,
  getUsersMeetingPageEpic,
  getCalendarMeetingRecordsEpic,
  getMeetingSummaryEpic,
  publishMeetingSummaryEpic,
  publishMeetingContentEpic,
  getMeetingIotDevicesEpic,
  revokeMeetingEpic,
  continueMeetingEpic,
  getContinueMeetingOperationEpic,
  getMeetingThemeConfigEpic,
  getMeetingCalendarDetailListEpic,
  getOrderSettingsEpic,
  getRecentlyListEpic
);
